import React from 'react';
import { 
  Calculator, 
  Settings, 
  FileDown, 
  Zap, 
  Layers
} from 'lucide-react';
import Checkbox from './Checkbox';
import ConfigurationPanel from './ConfigurationPanel';
import DirectLashingList from './DirectLashingList';

interface ManualLashingItem {
  id: string;
  ropeKey: string;
  count: number;
  position?: 'above' | 'below';
  angle?: number;
}

interface ManualLashingsState {
  forward: ManualLashingItem[];
  backward: ManualLashingItem[];
  right: ManualLashingItem[];
  left: ManualLashingItem[];
  loop: { ropeKey: string; count: number };
}

interface SidebarProps {
  woodenDunnageEnabled: boolean;
  setWoodenDunnageEnabled: (enabled: boolean) => void;
  stopperEnabled: boolean;
  setStopperEnabled: (enabled: boolean) => void;
  loopLashingEnabled: boolean;
  setLoopLashingEnabled: (enabled: boolean) => void;
  selectedLoopRope: string | null;
  setSelectedLoopRope: (rope: string | null) => void;
  selectedDirectRopes: Record<string, boolean>;
  setSelectedDirectRopes: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  halatTypes: any[];
  transportUnit: string;
  isVesselSelected: boolean;
  sortedLoopHalatOptions: any[];
  onCalculate: () => void;
  isCalculating: boolean;
  results: any;
  downloadProfessionalPDF: () => void;
  t: (key: string) => string;
  overweightPending?: boolean;
  calculationMode: 'optimizer' | 'calculator';
  setCalculationMode: (mode: 'optimizer' | 'calculator') => void;
  manualLashings: ManualLashingsState;
  setManualLashings: React.Dispatch<React.SetStateAction<ManualLashingsState>>;
  isAdmin: boolean;
  onCalculationReset?: () => void;
  lashingDisplayMode?: 'msl' | 'cordstrap';
  optimizerResultsTransferred?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  woodenDunnageEnabled,
  setWoodenDunnageEnabled,
  stopperEnabled,
  setStopperEnabled,
  loopLashingEnabled,
  setLoopLashingEnabled,
  selectedLoopRope,
  setSelectedLoopRope,
  selectedDirectRopes,
  setSelectedDirectRopes,
  halatTypes,
  transportUnit,
  isVesselSelected,
  sortedLoopHalatOptions,
  onCalculate,
  isCalculating,
  results,
  downloadProfessionalPDF,
  t,
  overweightPending,
  calculationMode,
  setCalculationMode,
  manualLashings,
  setManualLashings,
  isAdmin,
  onCalculationReset,
  lashingDisplayMode = 'msl',
  optimizerResultsTransferred = false,
}) => {
  const triggerCalculationReset = () => {
    if (onCalculationReset) {
      onCalculationReset();
    }
  };

  const handleLoopRopeSelect = (ropeKey: string | null) => {
    setSelectedLoopRope(ropeKey);
    triggerCalculationReset();
  };

  const handleDirectRopeChange = (ropeKey: string, selected: boolean) => {
    setSelectedDirectRopes(prev => ({
      ...prev,
      [ropeKey]: selected
    }));
    triggerCalculationReset();
  };

  const handleManualLashingChange = (
    direction: keyof ManualLashingsState,
    field: string,
    value: any,
    index?: number
  ) => {
    let nextValue: string | number = value;

    if (field === 'count') {
      const numericValue = typeof value === 'number'
        ? value
        : parseInt(value as string, 10) || 0;

      if (direction === 'loop') {
        const prevCount = manualLashings.loop.count;
        if (numericValue === 1) {
          if (prevCount === 0) {
            nextValue = 2;
          } else if (prevCount >= 2) {
            nextValue = 0;
          } else {
            nextValue = prevCount;
          }
        } else {
          nextValue = numericValue;
        }
      } else {
        if (numericValue % 2 === 1) {
          return;
        }
        nextValue = numericValue;
      }
    }

    setManualLashings(prev => {
      if (direction === 'loop') {
        return {
          ...prev,
          loop: {
            ...prev.loop,
            [field]: nextValue
          }
        };
      }

      if (typeof index === 'number') {
        const list = prev[direction] as ManualLashingItem[];
        const newList: ManualLashingItem[] = list.map((item, i) => {
          if (i !== index) return item;
          if (field === 'position') {
            return {
              ...item,
              position: (nextValue as string) === 'below' ? 'below' : 'above',
            };
          }
          return {
            ...item,
            [field]: nextValue,
          };
        });

        return {
          ...prev,
          [direction]: newList,
        };
      }
      return prev;
    });
    triggerCalculationReset();
  };

  const addLashing = (direction: keyof ManualLashingsState) => {
    if (direction === 'loop') return;
    setManualLashings(prev => {
      const currentList = (prev[direction] as ManualLashingItem[]) || [];
      return {
        ...prev,
        [direction]: [
          ...currentList,
          {
            id: Date.now().toString(),
            ropeKey: '',
            count: 0,
            position: 'above',
            angle: (direction === 'right' || direction === 'left' || transportUnit === 'breakbulk') ? 45 : undefined,
          },
        ],
      };
    });
  };

  const removeLashing = (direction: keyof ManualLashingsState, index: number) => {
    if (direction === 'loop') return;
    setManualLashings(prev => {
      const newList = [...(prev[direction] as Array<any>)];
      if (newList.length > 1) {
        newList.splice(index, 1);
        return {
          ...prev,
          [direction]: newList
        };
      }
      return prev;
    });
    triggerCalculationReset();
  };

  const compatibleRopes = (halatTypes || [])
    .filter(h => h.d_lashing_msl > 0 && h.compatible_units.includes(transportUnit));

  const transverseDirectDisabled = false; 
  const manualDirectDirections = (['forward', 'backward', 'right', 'left'] as const)
    .filter(dir => !(transverseDirectDisabled && (dir === 'right' || dir === 'left')))
    .map(dir => dir as string);

  // Updated logic: Show PDF button ONLY if results exist AND we're NOT in the intermediate "just transferred" manual state
  const isPdfButtonActive = results && (calculationMode === 'optimizer' || !optimizerResultsTransferred);

  return (
    <aside className="w-full bg-zinc-50 dark:bg-[#182235]/95 border-r border-zinc-200 dark:border-slate-400/20 flex flex-col shrink-0 z-20 h-full overflow-hidden shadow-xl shadow-zinc-200/50 dark:shadow-[0_16px_42px_-28px_rgba(2,6,23,0.9)] dark:backdrop-blur-xl">
      
      <div className="flex-1 flex flex-col overflow-y-auto custom-scrollbar p-3 space-y-4">
        
        {/* 1. MODE SWITCH */}
        <div className="space-y-3">
          <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] px-1">{t('operation_mode')}</label>
          <div className="bg-slate-200/50 dark:bg-white/5 p-1.5 rounded-lg flex gap-1.5 border border-slate-200 dark:border-white/10">
            <button
              onClick={() => setCalculationMode('optimizer')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-xs font-bold transition-all duration-300 ${calculationMode === 'optimizer'
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/30'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/10'
                }`}
            >
              <Zap className={`w-4 h-4 ${calculationMode === 'optimizer' ? 'text-white' : 'text-slate-400'}`} />
              {t('optimizer_mode')}
            </button>
            <button
              onClick={() => setCalculationMode('calculator')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-xs font-bold transition-all duration-300 ${calculationMode === 'calculator'
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/30'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/10'
                }`}
            >
              <Calculator className={`w-4 h-4 ${calculationMode === 'calculator' ? 'text-white' : 'text-slate-400'}`} />
              {t('manual_mode')}
            </button>
          </div>
        </div>

        {/* 2. PRIMARY ACTION (Desktop only, hidden on mobile as Floating Bottom Bar handles it) */}
        <div className="pt-2 hidden lg:block">
          <button
            onClick={isPdfButtonActive ? downloadProfessionalPDF : onCalculate}
            disabled={isCalculating}
            className={`w-full py-4 px-6 font-bold text-sm rounded-xl shadow-xl transition-all duration-300 flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 group border ${
              isPdfButtonActive 
                ? 'bg-white dark:bg-white/10 hover:bg-slate-50 dark:hover:bg-white/20 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-white/10 shadow-orange-500/10' 
                : 'bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white border-orange-400/20 shadow-orange-600/40'
            }`}
          >
            {isPdfButtonActive ? (
              <FileDown className="w-5 h-5 group-hover:bounce" />
            ) : (
              <Zap className={`w-5 h-5 transition-all ${isCalculating ? 'animate-pulse' : 'group-hover:scale-110 group-hover:rotate-12'}`} />
            )}
            <span className="tracking-wide uppercase text-center">
              {isPdfButtonActive 
                ? t('download_pdf')
                : isCalculating 
                  ? t('common.processing') 
                  : overweightPending 
                    ? t('continue_with_overweight') 
                    : t('calculate')}
            </span>
            {isPdfButtonActive && <div className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400 animate-pulse"></div>}
          </button>
        </div>

        {/* 3. CONFIGURATION SECTION */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-1 mb-2">
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">{t('configuration_panel')}</span>
          </div>
          <div className="bg-white dark:bg-[#202c42] rounded-xl border border-slate-200 dark:border-slate-400/20 overflow-hidden shadow-sm">
            <ConfigurationPanel
              woodenDunnageEnabled={woodenDunnageEnabled}
              setWoodenDunnageEnabled={setWoodenDunnageEnabled}
              stopperEnabled={stopperEnabled}
              setStopperEnabled={setStopperEnabled}
              loopLashingEnabled={loopLashingEnabled}
              setLoopLashingEnabled={setLoopLashingEnabled}
              selectedLoopRope={selectedLoopRope}
              setSelectedLoopRope={handleLoopRopeSelect}
              isVesselSelected={isVesselSelected}
              calculationMode={calculationMode}
              sortedLoopHalatOptions={sortedLoopHalatOptions}
              manualLashings={manualLashings}
              handleManualLashingChange={handleManualLashingChange}
              t={t}
              isAdmin={isAdmin}
              transportUnit={transportUnit}
              lashingDisplayMode={lashingDisplayMode}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2 px-1 mb-2">
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">{t('direct_lashing')}</span>
          </div>
          {calculationMode === 'optimizer' ? (
            <div className="space-y-3 bg-white dark:bg-[#202c42] p-4 rounded-xl border border-slate-200 dark:border-slate-400/20 shadow-sm">
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-black tracking-widest mb-3">{t('direct_lashing_selection')}</p>
                <div className="grid grid-cols-1 gap-1">
                    {compatibleRopes.map(h => {
                        const ropeKey = `${h.name}-${h.toka}`;
                        return (
                            <Checkbox
                                key={ropeKey}
                                label={lashingDisplayMode === 'cordstrap' && h.cordstrap_name ? h.cordstrap_name : (h.msl_tur || h.name)}
                                checked={!!selectedDirectRopes[ropeKey]}
                                onChange={(selected) => handleDirectRopeChange(ropeKey, selected)}
                                compact
                            />
                        );
                    })}
                </div>
            </div>
          ) : (
            <DirectLashingList
                manualDirectDirections={manualDirectDirections}
                manualLashings={manualLashings}
                compatibleRopes={compatibleRopes}
                addLashing={addLashing}
                removeLashing={removeLashing}
                handleManualLashingChange={handleManualLashingChange}
                t={t}
                isAdmin={isAdmin}
                lashingDisplayMode={lashingDisplayMode}
                transportUnit={transportUnit}
            />
          )}
        </div>
      </div>


    </aside>
  );
};

export default Sidebar;
